/**
 * Growth-step timeline — typical patterns by planting type, not predicted dates.
 */

import {
  blendKeyFromAnswers,
  catalogBlendDisplayName,
  sanitizeBlendKey,
} from './plantingPlanDisplay';

const RUST = '#b4501c';
const LODEN = '#3b4e3a';
const CREAM = '#efe7d2';
const MUST = '#c08a20';

function cropNames(parts = []) {
  return parts.map((part) => {
    const name = Array.isArray(part) ? part[0] : part?.name;
    return String(name || '').replace(/\s*\(nurse\)\s*/i, '').toLowerCase();
  }).filter(Boolean);
}

function drainageFamily(place) {
  const d = String(place?.drain || '').toLowerCase();
  if (/poor/.test(d)) return 'poor';
  if (/well/.test(d)) return 'well';
  if (/moderat/.test(d)) return 'moderate';
  return 'unknown';
}

function textureFamily(place) {
  const blob = `${place?.soilTypeId || ''} ${place?.series || ''}`.toLowerCase();
  if (/\bsand/.test(blob) && !/clay/.test(blob)) return 'sand';
  if (/loam/.test(blob) && !/clay/.test(blob)) return 'loam';
  if (/clay|muck/.test(blob)) return 'clay';
  return 'unknown';
}

function parsePh(place) {
  const n = Number(place?.ph);
  return Number.isFinite(n) ? n : null;
}

function blendHasClover(names = []) {
  return names.some((n) => /clover/i.test(n));
}

const BLADE_HEIGHTS = Object.freeze([
  [10, 8, 12, 9, 11],
  [18, 22, 16, 20, 19],
  [30, 34, 28, 33, 31],
  [40, 44, 38, 43, 41],
  [46, 46, 44, 46, 45],
]);

function germLine(names) {
  const first = names[0] || 'seed';
  if (names.length > 2) {
    return `${first} typically emerges in 1–2 weeks if the seed zone stays moist. Later species in the mix are slower. Colder soil is slower. Typical, not a forecast.`;
  }
  return `${first} typically emerges in 1–2 weeks if the seed zone stays moist and soil is in range. Colder soil is slower. Typical, not a forecast.`;
}

function typicalClock(key, names) {
  const germ = germLine(names);
  const clocks = {
    rye: {
      plantLabel: 'FALL WINDOW',
      peakLabel: 'THROUGH WINTER',
      hero: 'IN THIS FALL WINDOW',
      stages: [
        ['PLANTING WINDOW', 'In the ground', 'Work a seedbed and put seed at proper depth. Germination is seed-zone moisture and temperature — not rain the next afternoon.'],
        ['1–2 WEEKS', 'Germination', germ],
        ['4–8 WEEKS', 'First browse', 'Four to six inches of green is typical in a decent Southern fall. Deer find it. Do not hunt it yet — let it get ahead of them.'],
        ['THROUGH WINTER', 'Established', 'Roots set and the stand can take steady browse. Growth slows in the cold; the plot is holding, not racing.'],
        ['LATE FALL INTO SPRING', 'Peak forage', 'Rye and wheat carry winter. Clover in the mix typically takes the plot into spring after the grain burns back.'],
      ],
    },
    fall: {
      plantLabel: 'FALL WINDOW',
      peakLabel: 'THROUGH WINTER',
      hero: 'IN THIS FALL WINDOW',
      stages: [
        ['PLANTING WINDOW', 'In the ground', 'Work a seedbed and put seed at proper depth. Germination is seed-zone moisture and temperature — not rain the next afternoon.'],
        ['1–2 WEEKS', 'Germination', germ],
        ['4–8 WEEKS', 'First browse', 'Four to six inches of green is typical in a decent Southern fall. Deer find it. Do not hunt it yet — let it get ahead of them.'],
        ['THROUGH WINTER', 'Established', 'Roots set and the stand can take steady browse. Growth slows in the cold; the plot is holding, not racing.'],
        ['LATE FALL INTO SPRING', 'Peak draw', 'Grain carries the rut. Clover in the mix typically takes the plot into spring after the grain burns back.'],
      ],
    },
    spring: {
      plantLabel: 'AFTER LAST FROST',
      peakLabel: 'SUMMER HEAT',
      hero: 'AFTER LAST FROST',
      stages: [
        ['AFTER LAST FROST', 'In the ground', 'Warm-season seed wants warm soil. Plant after last frost into a workable seedbed. Moisture in the seed zone still decides germination.'],
        ['1–2 WEEKS', 'Germination', germ],
        ['3–6 WEEKS', 'Vining out', 'Peas are running and the sorghum is starting to shade them. Browse pressure starts here if the plot is small.'],
        ['MID-SUMMER', 'Full canopy', 'Ground is covered. The plot is producing more than deer can eat if the acreage was right.'],
        ['THROUGH SUMMER HEAT', 'Peak tonnage', 'This is the job of a summer plot. Frost ends it — there is no winter hold.'],
      ],
    },
    perennial: {
      plantLabel: 'FALL WINDOW',
      peakLabel: 'YEARS 2–5',
      hero: 'IN THIS FALL WINDOW',
      stages: [
        ['FALL WINDOW', 'In the ground', 'Fall planting. The nurse crop comes up first. Clover and chicory are slower — this is establishment, not a hunt plot in year one.'],
        ['1–2 WEEKS', 'Nurse crop up', germ],
        ['FIRST WINTER', 'Crowns set', 'Clover crowns and chicory taproots establish over winter. Expect little browse this year. Do not mow yet.'],
        ['YEAR 2', 'First production', 'This is when it becomes a perennial rather than an annual. Canopy fills if crowns made it through summer drought.'],
        ['YEARS 2–5', 'Hold with mowing', 'Mow it twice a year and it typically holds three to five seasons. Overseed thin spots each fall.'],
      ],
    },
    turkey: {
      plantLabel: 'PLANTING WINDOW',
      peakLabel: 'BUGGING SEASON',
      hero: 'IN THIS PLANTING WINDOW',
      stages: [
        ['PLANTING WINDOW', 'In the ground', 'Wheat and clover want a cool window; sorghum wants warm soil. Match the mix to the season you chose. Seed-zone moisture still rules germination.'],
        ['1–2 WEEKS', 'Germination', germ],
        ['4–8 WEEKS', 'Cover at ground level', 'Enough structure for poults to move under, open enough that they are not fighting through it.'],
        ['LATE SPRING–SUMMER', 'Bugging season', 'Insects working the clover. This is what the plot is actually for — poults eat bugs, not seed.'],
        ['LATE SUMMER INTO FALL', 'Seed heads standing', 'Grain heads up and holding. Leave it standing through the fall.'],
      ],
    },
    soil: {
      plantLabel: 'FALL WINDOW',
      peakLabel: 'SPRING TERMINATE',
      hero: 'IN THIS FALL WINDOW',
      stages: [
        ['FALL WINDOW', 'In the ground', 'Rye germinates in cool soil. This planting is working on the dirt — nothing to hunt here.'],
        ['1–2 WEEKS', 'Germination', germ],
        ['FALL INTO WINTER', 'Canopy closed', 'Rye is typically knee high and radish tops are shading the ground by late fall under decent moisture.'],
        ['THROUGH WINTER', 'Radish sizing', 'Taproots push through the pan. Leave them in the ground; the channel they leave is the point.'],
        ['EARLY SPRING', 'Terminate and plant', 'Roll or spray the rye and plant into the residue. Typical timing is before a spring cash or food-plot seeding — not a calendar Tuesday.'],
      ],
    },
  };
  return clocks[key] || clocks.fall;
}

function moistureRisk(place) {
  const drain = drainageFamily(place);
  const texture = textureFamily(place);
  if (drain === 'poor' || texture === 'clay') {
    return {
      title: 'Saturated seed zone',
      color: '#4e7ea8',
      text: 'On poorly drained ground, seed can rot before it germinates. Stay off the ponding end and wait for a workable day — oxygen in the seed zone matters as much as water.',
    };
  }
  if (drain === 'well' || texture === 'sand') {
    return {
      title: 'Dry seed zone',
      color: MUST,
      text: 'Sand and well-drained ground lose moisture fast. Small seed needs the seed zone to stay damp through imbibition. If it dries out, wait for the next front rather than seeding into dust.',
    };
  }
  return {
    title: 'Seed-zone moisture in week one',
    color: MUST,
    text: 'Germination is moisture and temperature in the top inch, not a calendar date. If the seed zone dries out, wait for the next front rather than seeding into dust.',
  };
}

function buildRisks(place, names, key) {
  const risks = [moistureRisk(place)];
  const ph = parsePh(place);
  if (ph != null && ph < 5.6 && blendHasClover(names)) {
    risks.push({
      title: 'Acid soil and clover',
      color: RUST,
      text: `At pH ${ph.toFixed(1)}, phosphorus locks up and clover will not fix nitrogen well. Lime does more here than planting a week earlier.`,
    });
  } else if (drainageFamily(place) === 'poor') {
    risks.push({
      title: 'Standing water on the low end',
      color: '#4e7ea8',
      text: 'Poorly drained ground ponds. Plan on losing the lowest corner or leave it out — that is drainage class, not a wet year.',
    });
  } else {
    risks.push({
      title: 'A dry stretch after planting',
      color: MUST,
      text: 'A rainless two weeks after seeding is the usual failure. Push planting to the next front rather than guessing a Tuesday.',
    });
  }

  if (key === 'soil') {
    risks.push({
      title: 'Terminate on time',
      color: LODEN,
      text: 'Rye that goes to head is harder to kill and steals the next planting. Typical terminate window is early spring, before a warm-season seeding.',
    });
  } else if (key === 'perennial') {
    risks.push({
      title: 'Browse in year one',
      color: RUST,
      text: 'Crowns are not set yet. Heavy browse the first fall can wipe the planting. Fence or let the nurse crop take the hit.',
    });
  } else {
    risks.push({
      title: 'Browse before the stand is ahead',
      color: RUST,
      text: 'A small plot in heavy deer country gets eaten to dirt before it establishes. Fence the first few weeks or go bigger — that is pressure, not a day-25 clock.',
    });
  }
  return risks.slice(0, 3);
}

export function typicalPlantLabel(answers = {}, place = null, preferKey = '') {
  const key = blendKeyFromAnswers(answers, preferKey);
  return typicalClock(key, []).plantLabel;
}

export function buildGrowthModel({
  answers = {},
  place = null,
  acres = 0,
  saved = false,
  preferKey = '',
  preview = null,
} = {}) {
  const blend = preview?.blend;
  const key = sanitizeBlendKey(blend?.key) || blendKeyFromAnswers(answers, preferKey);
  const recName = String(blend?.name || catalogBlendDisplayName(key) || '').trim();
  const names = cropNames(blend?.parts || []);
  const clock = typicalClock(key, names);

  const stages = clock.stages.map(([when, title, text], i) => ({
    num: String(i + 1),
    when,
    title,
    text,
    bg: i === 0 ? RUST : LODEN,
    fg: CREAM,
    rule: i === 4 ? 'transparent' : LODEN,
    blades: BLADE_HEIGHTS[i].map((h) => ({
      h: `${h}px`,
      color: i >= 3 ? LODEN : i >= 1 ? '#5c7357' : '#8b8367',
    })),
  }));

  const acresLabel = Number.isFinite(acres) && acres > 0
    ? (Math.round(acres * 10) / 10).toFixed(1)
    : '—';

  return {
    accent: 'TYPICAL',
    hero: clock.hero,
    bestDayShort: clock.hero,
    stages,
    risks: buildRisks(place, names, key),
    summary: [
      { label: 'ACRES', value: acresLabel },
      { label: 'BLEND', value: recName },
      { label: 'PLANT', value: clock.plantLabel },
      { label: 'PEAK', value: clock.peakLabel },
    ],
    saved: Boolean(saved),
    recName,
    plantShort: clock.plantLabel,
  };
}

export function growthContinueLabel(saved, { updating = false } = {}) {
  if (saved) return 'GO TO MY PLANS';
  if (updating) return 'UPDATE THIS PLAN';
  return 'SAVE THIS PLAN';
}
